import { ActionTree } from 'vuex'
import get from 'lodash.get'
import {
  KnowledgeMutation,
  KnowledgeMutationSetBasesPayload,
  KnowledgeMutationSelectBasePayload,
  KnowledgeMutationSetBlocks,
} from './mutations'
import { KnowledgeState } from './state'
import { RootState } from '../../state'
import knowledgeApiService from '@/services/knowledge'
import {
  KnowledgeBase,
  KnowledgeBlock,
  PatchKnowledgeBasePayload,
  CreateKnowledgeBaseRequest,
  UpdateKnowledgeBaseRequest,
  DeleteKnowledgeBaseRequest,
  PublishKnowledgeBaseRequest,
  UnpublishKnowledgeBaseRequest,
  CreateKnowledgeSectionRequest,
  UpdateKnowledgeSectionRequest,
  DeleteKnowledgeSectionRequest,
  GetKnowledgeBlocksRequest,
  CreateKnowledgeBlockRequest,
  UpdateKnowledgeBlockRequest,
  DeleteKnowledgeBlockRequest,
} from '@/models/knowledge'

export enum KnowledgeAction {
  SetSelectedBaseId = 'SetSelectedBaseId',
  GetBases = 'GetBases',
  PatchBase = 'PatchBase',
  CreateBase = 'CreateBase',
  UpdateBase = 'UpdateBase',
  DeleteBase = 'DeleteBase',
  PublishBase = 'PublishBase',
  UnpublishBase = 'UnpublishBase',
  CreateSection = 'CreateSection',
  UpdateSection = 'UpdateSection',
  DeleteSection = 'DeleteSection',
  GetBlocks = 'GetBlocks',
  CreateBlock = 'CreateBlock',
  UpdateBlock = 'UpdateBlock',
  DeleteBlock = 'DeleteBlock',
  ToggleSection = 'ToggleSection',
  ToggleBlock = 'ToggleBlock',
}

export const actions: ActionTree<KnowledgeState, RootState> = {
  [KnowledgeAction.SetSelectedBaseId](context, baseId: string) {
    const { getActiveCompany, getCompanyIsPro } = context.rootGetters
    const { getKnowledgeBases, getKnowledgeBaseById } = context.getters

    const activeCompanyId = getActiveCompany?.id

    const isRequiredPro =
      baseId &&
      !getCompanyIsPro &&
      activeCompanyId === getKnowledgeBaseById(baseId)?.company_id

    const data = isRequiredPro
      ? getKnowledgeBases.find(
          (base: KnowledgeBase) => base.company_id !== activeCompanyId,
        )?.id ?? ''
      : baseId

    const payload: KnowledgeMutationSelectBasePayload = {
      data,
    }
    context.commit(KnowledgeMutation.SetSelectedBaseId, payload)
  },

  async [KnowledgeAction.GetBases](context) {
    const response = await knowledgeApiService.getKnowledgeBases()
    context.commit(KnowledgeMutation.SetBases, response)

    const selectedBaseId = context.getters.getSelectedKnowledgeBaseId as string
    const firstBaseId = get(response, 'data[0].id')

    if (!selectedBaseId && firstBaseId) {
      context.dispatch(KnowledgeAction.SetSelectedBaseId, firstBaseId)
    }

    return response
  },

  [KnowledgeAction.PatchBase](context, payload: PatchKnowledgeBasePayload) {
    const { id, data } = payload

    const bases = context.getters.getKnowledgeBases as KnowledgeBase[]
    const baseIndex = bases.findIndex(b => b.id === id)

    if (baseIndex >= 0) {
      const newBases: KnowledgeMutationSetBasesPayload = {
        data: [...bases],
      }
      newBases.data[baseIndex] = {
        ...newBases.data[baseIndex],
        ...data,
      }
      context.commit(KnowledgeMutation.SetBases, newBases)
    }
  },

  async [KnowledgeAction.CreateBase](
    context,
    payload: CreateKnowledgeBaseRequest,
  ) {
    const response = await knowledgeApiService.createKnowledgeBase(payload)

    const bases = context.getters.getKnowledgeBases as KnowledgeBase[]
    const newBases: KnowledgeMutationSetBasesPayload = {
      data: [...bases, response.data],
    }
    context.commit(KnowledgeMutation.SetBases, newBases)
    context.dispatch(KnowledgeAction.SetSelectedBaseId, response.data.id)

    return response
  },

  async [KnowledgeAction.UpdateBase](
    context,
    payload: UpdateKnowledgeBaseRequest,
  ) {
    const response = await knowledgeApiService.updateKnowledgeBase(payload)

    const patchData: PatchKnowledgeBasePayload = {
      id: payload.id,
      data: { title: payload.title },
    }
    context.dispatch(KnowledgeAction.PatchBase, patchData)

    return response
  },

  async [KnowledgeAction.DeleteBase](
    context,
    payload: DeleteKnowledgeBaseRequest,
  ) {
    const response = await knowledgeApiService.deleteKnowledgeBase(payload)

    const bases = context.getters.getKnowledgeBases as KnowledgeBase[]
    const newBases: KnowledgeMutationSetBasesPayload = {
      data: bases.filter(b => b.id !== payload.id),
    }
    context.commit(KnowledgeMutation.SetBases, newBases)

    const selectedBaseId = context.getters.getSelectedKnowledgeBaseId as string
    const firstBaseId = get(context.getters.getKnowledgeBases, '[0].id')

    if (selectedBaseId === payload.id && firstBaseId) {
      context.dispatch(KnowledgeAction.SetSelectedBaseId, firstBaseId)
    }

    return response
  },

  async [KnowledgeAction.PublishBase](
    context,
    payload: PublishKnowledgeBaseRequest,
  ) {
    const response = await knowledgeApiService.publishKnowledgeBase(payload)

    const patchData: PatchKnowledgeBasePayload = {
      id: payload.id,
      data: { published: true },
    }
    context.dispatch(KnowledgeAction.PatchBase, patchData)

    return response
  },

  async [KnowledgeAction.UnpublishBase](
    context,
    payload: UnpublishKnowledgeBaseRequest,
  ) {
    const response = await knowledgeApiService.unpublishKnowledgeBase(payload)

    const patchData: PatchKnowledgeBasePayload = {
      id: payload.id,
      data: { published: false },
    }
    context.dispatch(KnowledgeAction.PatchBase, patchData)

    return response
  },

  async [KnowledgeAction.CreateSection](
    context,
    payload: CreateKnowledgeSectionRequest,
  ) {
    const response = await knowledgeApiService.createKnowledgeSection(payload)

    const bases = context.getters.getKnowledgeBases as KnowledgeBase[]
    const baseIndex = bases.findIndex(b => b.id === payload.id)

    if (baseIndex >= 0) {
      const newBases: KnowledgeMutationSetBasesPayload = {
        data: [...bases],
      }

      const base = bases[baseIndex]
      const newBase = {
        ...base,
        sections: [...(base.sections || []), response.data],
      }

      newBases.data[baseIndex] = newBase
      context.commit(KnowledgeMutation.SetBases, newBases)
    }

    return response
  },

  async [KnowledgeAction.UpdateSection](
    context,
    payload: UpdateKnowledgeSectionRequest,
  ) {
    const response = await knowledgeApiService.updateKnowledgeSection(payload)

    const bases = context.getters.getKnowledgeBases as KnowledgeBase[]
    const baseIndex = bases.findIndex(b => b.id === payload.baseId)

    if (baseIndex >= 0) {
      const newBases: KnowledgeMutationSetBasesPayload = {
        data: [...bases],
      }
      const base = bases[baseIndex]
      const baseSections = base.sections || []

      const sectionIndex = baseSections.findIndex(
        s => s.id === payload.sectionId,
      )

      if (sectionIndex >= 0) {
        const newSections = [...baseSections]
        newSections[sectionIndex] = {
          ...newSections[sectionIndex],
          title: payload.title,
        }
        const newBase = {
          ...base,
          sections: newSections,
        }
        newBases.data[baseIndex] = newBase
        context.commit(KnowledgeMutation.SetBases, newBases)
      }
    }

    return response
  },

  async [KnowledgeAction.DeleteSection](
    context,
    payload: DeleteKnowledgeSectionRequest,
  ) {
    const response = await knowledgeApiService.deleteKnowledgeSection(payload)

    const bases = context.getters.getKnowledgeBases as KnowledgeBase[]
    const baseIndex = bases.findIndex(b => b.id === payload.baseId)

    if (baseIndex >= 0) {
      const newBases: KnowledgeMutationSetBasesPayload = {
        data: [...bases],
      }

      const base = bases[baseIndex]
      const newBase = {
        ...base,
        sections: (base.sections || []).filter(s => s.id !== payload.sectionId),
      }

      newBases.data[baseIndex] = newBase
      context.commit(KnowledgeMutation.SetBases, newBases)
    }

    return response
  },

  async [KnowledgeAction.GetBlocks](
    context,
    payload: GetKnowledgeBlocksRequest,
  ) {
    const response = await knowledgeApiService.getKnowledgeBlocks(payload)

    const blocks = context.getters.getKnowledgeBlocks as Record<
      string,
      KnowledgeBlock[]
    >
    const newBlocks: KnowledgeMutationSetBlocks = {
      data: { ...blocks },
    }
    newBlocks.data[payload.sectionId] = response.data || []
    context.commit(KnowledgeMutation.SetBlocks, newBlocks)

    return response
  },

  async [KnowledgeAction.CreateBlock](
    context,
    payload: CreateKnowledgeBlockRequest,
  ) {
    const response = await knowledgeApiService.createKnowledgeBlock(payload)

    const blocks = context.getters.getKnowledgeBlocks as Record<
      string,
      KnowledgeBlock[]
    >
    const newBlocks: KnowledgeMutationSetBlocks = {
      data: { ...blocks },
    }
    const blocksBySection = newBlocks.data[payload.sectionId] || []
    newBlocks.data[payload.sectionId] = [...blocksBySection, response.data]
    context.commit(KnowledgeMutation.SetBlocks, newBlocks)

    return response
  },

  async [KnowledgeAction.UpdateBlock](
    context,
    payload: UpdateKnowledgeBlockRequest,
  ) {
    const response = await knowledgeApiService.updateKnowledgeBlock(payload)

    const blocks = context.getters.getKnowledgeBlocks as Record<
      string,
      KnowledgeBlock[]
    >
    const blockIndex = (blocks[payload.sectionId] || []).findIndex(
      b => b.id === payload.blockId,
    )

    if (blockIndex >= 0) {
      const newBlocks: KnowledgeMutationSetBlocks = {
        data: { ...blocks },
      }
      newBlocks.data[payload.sectionId][blockIndex] = {
        ...newBlocks.data[payload.sectionId][blockIndex],
        title: payload.title,
        content: payload.content,
      }
      context.commit(KnowledgeMutation.SetBlocks, newBlocks)
    }

    return response
  },

  async [KnowledgeAction.DeleteBlock](
    context,
    payload: DeleteKnowledgeBlockRequest,
  ) {
    const response = await knowledgeApiService.deleteKnowledgeBlock(payload)

    const blocks = context.getters.getKnowledgeBlocks as Record<
      string,
      KnowledgeBlock[]
    >
    const newBlocks: KnowledgeMutationSetBlocks = {
      data: { ...blocks },
    }
    const blocksBySection = newBlocks.data[payload.sectionId] || []
    newBlocks.data[payload.sectionId] = blocksBySection.filter(
      b => b.id !== payload.blockId,
    )
    context.commit(KnowledgeMutation.SetBlocks, newBlocks)

    return response
  },

  [KnowledgeAction.ToggleSection](context, payload: string) {
    const openSections: string[] = context.getters.getOpenSectionIds
    const isOpen = openSections.includes(payload)
    const newOpenSections = isOpen
      ? openSections.filter(id => id !== payload)
      : [...openSections, payload]

    context.commit(KnowledgeMutation.SetOpenSectionId, newOpenSections)
  },

  [KnowledgeAction.ToggleBlock](context, payload: string) {
    const openBlocks: string[] = context.getters.getOpenBlockIds
    const isOpen = openBlocks.includes(payload)
    const newOpenBlocks = isOpen
      ? openBlocks.filter(id => id !== payload)
      : [...openBlocks, payload]

    context.commit(KnowledgeMutation.SetOpenBlockId, newOpenBlocks)
  },
}
